import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";

interface PropsType {
  userState: userState;
}

interface BookCafeSlotReservation {
  reservationId: string;
  reservDate: string;
  slot: string;
  reservState: string;
  communityPrice: number;
  cancelDt?: string;
}

interface ReservationGroup {
  reservationId: string;
  reservDate: string;
  slots: BookCafeSlotReservation[];
}

const mainApi = new HttpMainApi();
const addHour = (time: string) => `${String(Number(time.slice(0, 2)) + 1).padStart(2, "0")}:00`;
const formatDateLabel = (date: string) => `${date.slice(0, 4)}년 ${date.slice(4, 6)}월 ${date.slice(6, 8)}일`;
const slotStartMs = (date: string, slot: string) =>
  new Date(`${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${slot}:00+09:00`).getTime();
const kstDate = (ms: number) => new Date(ms + 9 * 60 * 60 * 1000).toISOString().slice(0, 10).replace(/-/g, "");
const activeSlots = (group: ReservationGroup) => group.slots.filter((slot) => slot.reservState === "신청");
const timeRange = (slots: BookCafeSlotReservation[]) => {
  const ranges: string[] = [];
  slots.forEach((row, index) => {
    if (index === 0 || addHour(slots[index - 1].slot) !== row.slot) ranges.push(row.slot);
    if (index === slots.length - 1 || addHour(row.slot) !== slots[index + 1].slot) {
      ranges[ranges.length - 1] += ` — ${addHour(row.slot)}`;
    }
  });
  return ranges.join(" · ");
};

const ReservationBookCafeEnd = ({ userState }: PropsType) => {
  const [rows, setRows] = useState<BookCafeSlotReservation[]>([]);
  const [serverNowMs, setServerNowMs] = useState(Date.now());
  const serverOffsetMs = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [expandedKey, setExpandedKey] = useState("");
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [confirmingKey, setConfirmingKey] = useState("");

  const loadReservations = useCallback(async () => {
    setIsLoading(true);
    setError("");
    setExpandedKey("");
    setSelectedSlots([]);
    setConfirmingKey("");
    try {
      const res = await mainApi.get_my_book_cafe_reservations({ userId: userState.userId });
      if (res.code !== "200" || !Array.isArray(res.response?.result)) throw new Error("load");
      setRows(res.response.result);
      const now = new Date(res.response.serverNow).getTime();
      if (Number.isFinite(now)) {
        serverOffsetMs.current = now - Date.now();
        setServerNowMs(now);
      }
    } catch {
      setRows([]);
      setError("예약 내역을 불러오지 못했습니다. 다시 시도해 주세요.");
    } finally {
      setIsLoading(false);
    }
  }, [userState.userId]);

  useEffect(() => { void loadReservations(); }, [loadReservations]);
  useEffect(() => {
    const timer = window.setInterval(() => setServerNowMs(Date.now() + serverOffsetMs.current), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const groups = useMemo(() => {
    const grouped = new Map<string, ReservationGroup>();
    rows.forEach((row) => {
      const key = `${row.reservationId}#${row.reservDate}`;
      const group = grouped.get(key) ?? { reservationId: row.reservationId, reservDate: row.reservDate, slots: [] };
      group.slots.push(row);
      grouped.set(key, group);
    });
    return Array.from(grouped.values()).map((group) => ({
      ...group, slots: group.slots.sort((a, b) => a.slot.localeCompare(b.slot)),
    }));
  }, [rows]);

  const isOngoing = (group: ReservationGroup) => activeSlots(group).some((row) =>
    slotStartMs(group.reservDate, row.slot) <= serverNowMs && slotStartMs(group.reservDate, addHour(row.slot)) > serverNowMs);
  const isCurrent = (group: ReservationGroup) => activeSlots(group).some((row) =>
    slotStartMs(group.reservDate, addHour(row.slot)) > serverNowMs);
  const firstStart = (group: ReservationGroup) => slotStartMs(group.reservDate, (activeSlots(group)[0] ?? group.slots[0]).slot);
  const currentGroups = groups.filter(isCurrent).sort((a, b) =>
    Number(isOngoing(b)) - Number(isOngoing(a)) || firstStart(a) - firstStart(b));
  const historyGroups = groups.filter((group) => !isCurrent(group)).sort((a, b) => firstStart(b) - firstStart(a));

  const getCancelableSuffix = (group: ReservationGroup) => {
    const active = activeSlots(group);
    const suffix: string[] = [];
    for (let index = active.length - 1; index >= 0; index -= 1) {
      const row = active[index];
      if (slotStartMs(group.reservDate, row.slot) <= serverNowMs || (suffix.length > 0 && addHour(row.slot) !== suffix[0])) break;
      suffix.unshift(row.slot);
    }
    return suffix;
  };

  const isSelectionCancelable = (group: ReservationGroup) => {
    const eligible = getCancelableSuffix(group);
    return selectedSlots.length > 0 && selectedSlots.length <= eligible.length &&
      eligible.slice(-selectedSlots.length).join() === selectedSlots.join();
  };

  const cancelSelectedSlots = async (group: ReservationGroup) => {
    if (isCancelling || !isSelectionCancelable(group)) return;
    setIsCancelling(true);
    setMessage("");
    try {
      const res = await mainApi.cancel_book_cafe_reservation_slots({
        reservationId: group.reservationId, slots: selectedSlots, userId: userState.userId,
        inputDt: getFormattedDateTime().compactDateTime,
      });
      const cancellationError = res.code === "200" ? "" : res.code === "606"
        ? "이미 시작된 시간은 취소할 수 없습니다. 예약 상태를 새로 확인했습니다."
        : res.code === "607" ? "마지막 예약 시간부터 연속해서만 취소할 수 있습니다."
        : res.response?.error_msg || "예약을 취소하지 못했습니다. 다시 시도해 주세요.";
      // Refresh retained times and charges from the server after every response.
      await loadReservations();
      if (cancellationError) setError(cancellationError);
      else setMessage("선택한 시간이 취소되었습니다.");
    } catch {
      await loadReservations();
      setError("취소 결과를 확인하지 못했습니다. 예약 내역을 다시 확인해 주세요.");
    } finally {
      setIsCancelling(false);
    }
  };

  const renderGroup = (group: ReservationGroup) => {
    const key = `${group.reservationId}#${group.reservDate}`;
    const active = activeSlots(group);
    const cancelled = group.slots.filter((row) => row.reservState === "취소");
    const suffix = getCancelableSuffix(group);
    const expanded = key === expandedKey;
    const selectionValid = expanded && isSelectionCancelable(group);
    const retained = active.filter((row) => !selectedSlots.includes(row.slot));
    const price = active.reduce((total, row) => total + Number(row.communityPrice || 0), 0);
    const remainingPrice = retained.reduce((total, row) => total + Number(row.communityPrice || 0), 0);
    const weekday = new Date(slotStartMs(group.reservDate, "12:00")).toLocaleDateString("ko-KR", { weekday: "short", timeZone: "Asia/Seoul" });
    const date = `${group.reservDate.slice(0, 4) !== kstDate(serverNowMs).slice(0, 4) ? `${group.reservDate.slice(0, 4)}년 ` : ""}${Number(group.reservDate.slice(4, 6))}월 ${Number(group.reservDate.slice(6, 8))}일 (${weekday})`;
    const relativeDate = group.reservDate === kstDate(serverNowMs) ? "오늘" : group.reservDate === kstDate(serverNowMs + 86400000) ? "내일" : "";
    const status = active.length === 0 ? "취소 완료" : isOngoing(group) ? "이용 중" : isCurrent(group) ? "예약 확정" : "이용 완료";
    return (
      <article className={`hp-book-cafe-booking${isOngoing(group) ? " hp-book-cafe-booking--ongoing" : ""}`} key={key}
        aria-label={`${formatDateLabel(group.reservDate)} 북카페 예약 ${timeRange(active.length ? active : group.slots)}`}>
        <div className="hp-book-cafe-booking__heading">
          <h2>{date}{relativeDate ? <small>{relativeDate}</small> : null}</h2>
          <span className="hp-book-cafe-booking__status">{status}</span>
        </div>
        <p className="hp-book-cafe-booking__time">{timeRange(active.length ? active : group.slots)}</p>
        <p className="hp-book-cafe-booking__duration">{active.length ? `총 ${active.length}시간 이용` : "예약이 취소되었습니다"}</p>
        <div className="hp-book-cafe-booking__footer">
          <p><span>{active.length ? "청구 예정" : "청구 금액"}</span><strong>{price.toLocaleString("ko-KR")}원</strong></p>
          {suffix.length > 0 || expanded ? (
            <Button variant="text" disabled={isCancelling} aria-expanded={expanded} aria-controls={`cancel-${key}`}
              onClick={() => { setExpandedKey(expanded ? "" : key); setSelectedSlots([]); }}>
              {expanded ? "취소 닫기" : "예약 취소"}
            </Button>
          ) : null}
        </div>
        {cancelled.length > 0 && active.length > 0 ? (
          <details className="hp-book-cafe-booking__cancelled"><summary>취소 내역 {cancelled.length}시간</summary><p>{timeRange(cancelled)} · 취소 완료</p></details>
        ) : null}
        {expanded ? (
          <section className="hp-book-cafe-booking__cancel" id={`cancel-${key}`} aria-label="예약 취소 설정">
            <h3>취소할 시간을 선택해 주세요</h3>
            <p>아직 시작하지 않은 마지막 시간부터 1시간 단위로 취소할 수 있습니다.</p>
            <div role="radiogroup" aria-label="취소할 시간">
              {suffix.map((_, index) => {
                const hours = index + 1;
                const slots = suffix.slice(-hours);
                const label = `${hours === active.length ? "전체" : "마지막"} ${hours}시간 취소`;
                const optionRows = active.filter((row) => slots.includes(row.slot));
                return <label className="hp-book-cafe-booking__option" key={hours}>
                  <input type="radio" name={`cancel-${key}`} checked={selectionValid && selectedSlots.length === hours} disabled={isCancelling}
                    onChange={() => setSelectedSlots(slots)} />
                  <span><strong>{label}</strong><span>{timeRange(optionRows)}</span></span>
                </label>;
              })}
            </div>
            {selectedSlots.length > 0 && !selectionValid ? <p role="status">이용시간이 시작되었습니다. 취소할 시간을 다시 선택해 주세요.</p> : null}
            {selectionValid ? (
              <section className="hp-book-cafe-booking__preview" aria-label="취소 후 예약 확인">
                <dl>
                  <div><dt>취소할 시간</dt><dd>{timeRange(active.filter((row) => selectedSlots.includes(row.slot)))}</dd></div>
                  <div><dt>남는 예약</dt><dd>{retained.length ? `${timeRange(retained)} · ${retained.length}시간` : "없음"}</dd></div>
                  <div><dt>취소 후 청구 예정</dt><dd>{remainingPrice.toLocaleString("ko-KR")}원</dd></div>
                </dl>
              </section>
            ) : null}
            <Button variant="contained" color="error" disabled={!selectionValid || isCancelling} onClick={() => setConfirmingKey(key)}>
              {isCancelling ? "취소 처리 중…" : "취소 확정"}
            </Button>
          </section>
        ) : null}
      </article>
    );
  };

  const confirmingGroup = groups.find((group) => `${group.reservationId}#${group.reservDate}` === confirmingKey);
  const confirmingActive = confirmingGroup ? activeSlots(confirmingGroup) : [];
  const retained = confirmingActive.filter((row) => !selectedSlots.includes(row.slot));
  const cancelled = confirmingActive.filter((row) => selectedSlots.includes(row.slot));
  const confirmationValid = confirmingGroup ? isSelectionCancelable(confirmingGroup) : false;

  return (
    <main className="hpoint-renewal hp-reservation-page hp-book-cafe-bookings">
      <header><h1 className="hp-page-title">북카페 예약 내역</h1><p className="hp-page-description">예약한 날짜와 이용시간을 확인하세요.</p></header>
      {message ? <Alert severity="success" role="status">{message}</Alert> : null}
      {error ? <Alert severity="error" action={<Button disabled={isCancelling} onClick={() => void loadReservations()}>다시 시도</Button>}>{error}</Alert> : null}
      {isLoading ? <div className="hp-book-cafe-bookings__loading" aria-label="예약 내역 불러오는 중"><CircularProgress /></div> : (
        <>
          {!error && groups.length === 0 ? <Alert severity="info">예약된 내용이 없습니다.</Alert> : null}
          {currentGroups.length ? <section className="hp-book-cafe-bookings__list" aria-label="이용 중 · 예정 예약">{currentGroups.map(renderGroup)}</section> : null}
          {historyGroups.length ? <details className="hp-book-cafe-bookings__history"><summary><span>지난 예약 · 취소 내역</span><span>{historyGroups.length}건</span></summary><div className="hp-book-cafe-bookings__list">{historyGroups.map(renderGroup)}</div></details> : null}
        </>
      )}
      <Dialog open={Boolean(confirmingGroup)} onClose={() => { if (!isCancelling) setConfirmingKey(""); }}
        fullWidth maxWidth="xs" aria-labelledby="book-cafe-cancel-title" aria-describedby="book-cafe-cancel-description"
        PaperProps={{ className: "hpoint-renewal hp-book-cafe-cancel-dialog" }}>
        <DialogTitle id="book-cafe-cancel-title">예약을 취소하시겠어요?</DialogTitle>
        <DialogContent>
          <p id="book-cafe-cancel-description">{confirmingGroup ? formatDateLabel(confirmingGroup.reservDate) : ""}</p>
          <section className="hp-book-cafe-cancel-dialog__available" aria-label="사용가능시간">
            <span>사용가능시간</span>
            <strong>{retained.length ? timeRange(retained).replace(/ — /g, " ~ ") : "없음"}</strong>
            <small>{retained.length ? `취소 후에도 ${retained.length}시간 이용할 수 있어요.` : "예약이 전체 취소됩니다."}</small>
          </section>
          <section className="hp-book-cafe-cancel-dialog__cancelled" aria-label="취소시간">
            <span>취소시간</span>
            <strong>{timeRange(cancelled).replace(/ — /g, " ~ ")}</strong>
          </section>
          {!confirmationValid && confirmingGroup ? <Alert severity="warning">이용시간이 시작되었습니다. 돌아가서 취소할 시간을 다시 선택해 주세요.</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isCancelling} onClick={() => setConfirmingKey("")}>돌아가기</Button>
          <Button variant="contained" color="error" disabled={!confirmationValid || isCancelling}
            onClick={() => { if (confirmingGroup) void cancelSelectedSlots(confirmingGroup); }}>
            {isCancelling ? "취소 처리 중…" : "예약 취소하기"}
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};

export default ReservationBookCafeEnd;
